import type {
  IChatConversationDetail,
  IChatLocalMessage,
  IChatMember,
} from "@/types/(my-space)/chat.interface";

/**
 * The marks on one of my messages:
 * - pending: not stored yet (clock)
 * - sent: stored on the server (one tick)
 * - delivered: reached every other member's device (two ticks)
 * - read: read by every other member (three ticks, in the read colour)
 */
export type TickState = "pending" | "sent" | "delivered" | "read";

type TickMember = Pick<
  IChatMember,
  "id" | "lastReadMessageId" | "lastDeliveredMessageId" | "joinedAt"
>;

/** Read implies delivered: the further of the two pointers. */
export const deliveredUpTo = (
  m: Pick<IChatMember, "lastReadMessageId" | "lastDeliveredMessageId">,
) => Math.max(m.lastDeliveredMessageId ?? 0, m.lastReadMessageId ?? 0);

/**
 * The members a message was meant for: everyone but me — and, outside a
 * direct chat, nobody who joined after it was sent (a joiner's pointers start
 * at the newest message, so they'd count as having everything before it).
 */
export const audience = <M extends Pick<IChatMember, "id" | "joinedAt">>(
  members: M[],
  message: Pick<IChatLocalMessage, "createdAt">,
  myUserId: number | undefined,
  isDirect: boolean,
) => {
  const sentAt = new Date(message.createdAt).getTime();
  return members.filter(
    (x) =>
      x.id !== myUserId &&
      (isDirect || !x.joinedAt || new Date(x.joinedAt).getTime() <= sentAt),
  );
};

/**
 * Tick state of one of MY messages. A self chat stays single-tick (there is no
 * one else — "read by everyone" would be an empty everyone); so does a message
 * whose audience the API didn't send (broadcast members hidden from me).
 */
export const tickState = (
  message: Pick<IChatLocalMessage, "id" | "createdAt" | "sendState">,
  members: TickMember[] | null | undefined,
  myUserId: number | undefined,
  isSelf: boolean,
  opts: { isDirect?: boolean } = {},
): TickState => {
  if (message.id <= 0 || message.sendState) return "pending";
  if (isSelf || !members?.length) return "sent";
  const others = audience(members, message, myUserId, !!opts.isDirect);
  if (!others.length) return "sent";
  if (others.every((x) => (x.lastReadMessageId ?? 0) >= message.id)) return "read";
  if (others.every((x) => deliveredUpTo(x) >= message.id)) return "delivered";
  return "sent";
};

/** `tickState` from a conversation detail (or none yet: just "sent"). */
export const tickStateIn = (
  chat: Pick<IChatConversationDetail, "type" | "isSelf" | "members"> | null | undefined,
  message: Pick<IChatLocalMessage, "id" | "createdAt" | "sendState">,
  myUserId: number | undefined,
): TickState =>
  tickState(message, chat?.members, myUserId, !!chat?.isSelf, {
    isDirect: chat?.type === "direct",
  });

/**
 * Message info's not-yet-read members, split into whose devices have it
 * (delivered) and whose don't (pending).
 */
export const splitDelivery = <T extends Pick<IChatMember, "lastReadMessageId" | "lastDeliveredMessageId">>(
  unread: T[],
  messageId: number,
) => {
  const delivered: T[] = [];
  const pending: T[] = [];
  for (const x of unread) (deliveredUpTo(x) >= messageId ? delivered : pending).push(x);
  return { delivered, pending };
};
